/* eslint-disable react/no-array-index-key */
import { PDFDownloadLink } from '@react-pdf/renderer';
import Link from 'next/link';
import React, { FC, Fragment, useEffect, useState, useContext } from 'react';
import { CSVLink } from 'react-csv';
import useIsBreakpoint from '../../../hooks/use-is-breakpoint/use-is-breakpoint';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import BreakpointEnum from '../../../models/breakpoint-enum';
import { DiscountStructureItemType } from '../../../models/discount-structure-item-type';
import { DiscountStructureType } from '../../../models/discount-structure-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import accountDiscountStructure from '../../../services/customer-api/endpoints/account/discount-structure';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';
import useUser from '../../providers/user/hooks/use-user.hook';
import DiscountStructurePdf from '../discount-structure-pdf/discount-structure-pdf';

const DiscountStructureTable: FC<DiscountStructureType> = (
  props: DiscountStructureType
) => {
  const {
    downloadDescriptionsLink,
    labels: {
      downloadCsvLinkLabel,
      groupColumnLabel,
      percentageColumnLabel,
      printLinkLabel,
      searchResultsPageLink,
    },
  } = props;

  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const [discounts, setDiscounts] = useState<DiscountStructureItemType[]>();
  const [discountsGroups, setDiscountsGroups] =
    useState<DiscountStructureItemType[][]>();
  const [pdfDiscountsGroups, setPdfDiscountsGroups] =
    useState<DiscountStructureItemType[][]>();
  const [numberOfColumns, setNumberOfColumns] = useState<number>(6);
  const getSessionId = useGetSessionId();
  const user = useUser();
  const breakpoint = useIsBreakpoint();
  const searchResultsPageUrl = searchResultsPageLink.url.endsWith('/')
    ? searchResultsPageLink.url.substring(
        0,
        searchResultsPageLink.url.length - 1
      )
    : searchResultsPageLink.url;

  const getGroupedDiscounts = (
    columns: number
  ): DiscountStructureItemType[][] => {
    const sliceAmount = Math.ceil(discounts?.length / columns);
    let currentSlice = 0;

    const groups: DiscountStructureItemType[][] = [];
    new Array(columns).fill(null).forEach(() => {
      groups.push(discounts.slice(currentSlice, currentSlice + sliceAmount));

      currentSlice += sliceAmount;
    });

    return groups;
  };

  useEffect(() => {
    getSessionId().then((sessionId) =>
      accountDiscountStructure(sessionId).then(
        (response: { group: number; percentage: number }[]) =>
          setDiscounts(response)
      )
    );
  }, []);

  useEffect(() => {
    if (breakpoint === BreakpointEnum.sm) {
      setNumberOfColumns(2);
    } else if (
      breakpoint === BreakpointEnum.md ||
      breakpoint === BreakpointEnum.lg
    ) {
      setNumberOfColumns(4);
    } else if (
      breakpoint === BreakpointEnum.xl ||
      breakpoint === BreakpointEnum.xxl
    ) {
      setNumberOfColumns(6);
    }

    if (!discounts) {
      return;
    }

    const groups = getGroupedDiscounts(numberOfColumns);

    setDiscountsGroups(groups);

    if (!pdfDiscountsGroups) {
      setPdfDiscountsGroups(getGroupedDiscounts(6));
    }
  }, [breakpoint, discounts, numberOfColumns]);

  if (!discounts || !discountsGroups || !user) {
    return <LoadingSpinner className="h-96" />;
  }

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div className="flex mb-4 md:mb-0">
          <CSVLink
            className="flex items-center focus:outline-none"
            data={discounts}
            filename="Flowtech UK Discount Structure.csv"
            headers={[
              { label: 'Group', key: 'group' },
              { label: '%', key: 'percentage' },
            ]}
            onClick={() =>
              pushToDataLayer(GtmEventNameEnum.DetailsDiscountDloadCsv)
            }
          >
            <IcomoonIcon color="french-blue" name="download" size="base" />
            <span className="ml-2">{downloadCsvLinkLabel}</span>
          </CSVLink>

          <PDFDownloadLink
            className="flex items-center ml-8 focus:outline-none"
            document={
              <DiscountStructurePdf
                {...props}
                discountsGroups={pdfDiscountsGroups}
                user={user}
              />
            }
            fileName="Flowtech UK Discount Structure.pdf"
          >
            <button
              className="flex items-center focus:outline-none"
              onClick={() =>
                pushToDataLayer(GtmEventNameEnum.DetailsDiscountPrint)
              }
              type="button"
            >
              <IcomoonIcon color="french-blue" name="fax" size="base" />
              <span className="ml-2">{printLinkLabel}</span>
            </button>
          </PDFDownloadLink>
        </div>

        <Link href={downloadDescriptionsLink.url} passHref>
          <a
            className="flex items-center"
            href="replace"
            onClick={() =>
              pushToDataLayer(GtmEventNameEnum.DetailsDiscountDloadDesc)
            }
            target="_blank"
          >
            <IcomoonIcon color="french-blue" name="download" size="base" />
            <span className="ml-2">{downloadDescriptionsLink.name}</span>
          </a>
        </Link>
      </div>

      <hr className="border-grey-100 mb-6" />

      <table className="table-fixed w-full">
        <thead>
          <tr className="border-b-2">
            {new Array(numberOfColumns)
              .fill(null)
              .map((value: null, index: number) => (
                <Fragment key={`heading-${index}`}>
                  <td className="font-bold pl-2 py-2">{groupColumnLabel}</td>

                  <td className="text-center py-2">{percentageColumnLabel}</td>
                </Fragment>
              ))}
          </tr>
        </thead>

        <tbody>
          {discountsGroups[0].map((discountGroup, index: number) => (
            <tr key={`row-${index}`}>
              {discountsGroups.map(
                (group: DiscountStructureItemType[], dataIndex: number) => (
                  <Fragment key={`data-${dataIndex}`}>
                    <td className="border-b border-l border-grey-100 pl-2 py-3">
                      {group[index] && (
                        <Link
                          href={`${searchResultsPageUrl}?pdg=${group[index].group}`}
                          passHref
                        >
                          <a
                            className="text-french-blue hover:underline"
                            href="replace"
                          >
                            {group[index].group}
                          </a>
                        </Link>
                      )}
                    </td>

                    <td className="border-b border-r border-grey-100  py-3 text-center">
                      {group[index]?.percentage}
                    </td>
                  </Fragment>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default DiscountStructureTable;
